import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import InformationsEnfantForm from '../components/InformationsEnfantForm';
import api from '../services/api';
import logger from '../utils/logger';

const InformationsEnfant = () => {
  const { inscriptionId } = useParams();
  const navigate = useNavigate();
  const [inscription, setInscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    loadInscription();
  }, [inscriptionId]);

  const loadInscription = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/inscriptions/${inscriptionId}/`);
      setInscription(response.data);
      setError(''); // Clear any previous errors
    } catch (err) {
      logger.error('Erreur chargement inscription:', err);
      logger.error('Détails erreur:', err.response);

      if (err.response?.status === 404) {
        setError(`Inscription #${inscriptionId} introuvable. Vérifiez que cette inscription existe et vous appartient.`);
      } else if (err.response?.status === 403) {
        setError('Vous n\'avez pas accès à cette inscription.');
      } else if (err.response?.status === 401) {
        setError('Vous devez être connecté pour accéder à cette page.');
      } else {
        setError(`Erreur lors du chargement de l'inscription : ${err.message}`);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSuccess = () => {
    // Rediriger vers la page de détail de l'inscription
    navigate(`/inscriptions/${inscriptionId}`);
  };

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto py-8 px-4">
        <div className="flex justify-center items-center min-h-[400px]">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-4xl mx-auto py-8 px-4">
        <div className="bg-red-50 border-l-4 border-red-400 p-4 mb-4">
          <p className="text-sm text-red-700">{error}</p>
        </div>
        <button
          onClick={() => navigate(`/inscriptions/${inscriptionId}`)}
          className="bg-gray-600 text-white px-4 py-2 rounded-md hover:bg-gray-700"
        >
          ← Retour à l'inscription
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto py-8 px-4">
      <button
        onClick={() => navigate(`/inscriptions/${inscriptionId}`)}
        className="mb-4 text-primary-600 hover:text-primary-700 flex items-center"
      >
        <svg className="w-5 h-5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
        </svg>
        Retour à l'inscription
      </button>

      <div className="bg-white shadow rounded-lg p-6 mb-6">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          Informations complémentaires
        </h1>
        {inscription && (
          <>
            <p className="text-gray-600 mb-1">
              <span className="font-medium">Enfant :</span> {inscription.enfant_details?.prenom} {inscription.enfant_details?.nom}
            </p>
            <p className="text-gray-600 mb-4">
              <span className="font-medium">Séjour :</span> {inscription.sejour_details?.titre}
            </p>

            {!inscription.is_infos_complete && (
              <div className="bg-blue-50 border-l-4 border-blue-400 p-4">
                <p className="text-sm text-blue-700">
                  Veuillez compléter toutes les informations obligatoires ci-dessous.
                  Ces informations sont nécessaires pour finaliser l'inscription.
                </p>
              </div>
            )}

            {inscription.statut === 'EN_ATTENTE_VALIDATION_PARTENAIRE' && (
              <div className="bg-orange-50 border-l-4 border-orange-400 p-4">
                <p className="text-sm text-orange-700">
                  Votre inscription est en attente de validation par nos partenaires sociaux.
                  Vous pourrez compléter ces informations une fois l'inscription validée.
                </p>
              </div>
            )}
          </>
        )}
      </div>

      {inscription && inscription.statut !== 'EN_ATTENTE_VALIDATION_PARTENAIRE' && (
        <InformationsEnfantForm
          inscriptionId={inscriptionId}
          onSuccess={handleSuccess}
          readOnly={inscription.statut === 'ANNULEE'}
        />
      )}
    </div>
  );
};

export default InformationsEnfant;
